/**
 * Tiny i18n provider built on React context.
 *
 * We deliberately avoid react-i18next/formatjs - Wave A only needs a
 * lookup table with string interpolation. If Wave B grows the scope
 * (plurals, ICU, lazy bundles) we can swap the provider without touching
 * call sites.
 *
 * Usage:
 *   const t = useT();
 *   t('common.confirm');                     // "Confirm"
 *   t('placeholder.title', { page: 'Deals' })// "Deals coming soon"
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { en, type Messages } from './en';
import { tr } from './tr';

export type Locale = 'en' | 'tr';

const BUNDLES: Record<Locale, Messages> = { en, tr };

type Leaves<T> = T extends string
  ? []
  : {
      [K in keyof T]-?: [K, ...Leaves<T[K]>];
    }[keyof T];

type Join<T extends readonly unknown[], D extends string> = T extends []
  ? ''
  : T extends [infer F extends string]
    ? F
    : T extends [infer F extends string, ...infer R]
      ? `${F}${D}${Join<R extends readonly string[] ? R : never, D>}`
      : never;

export type MessageKey = Join<Leaves<Messages>, '.'>;

function resolve(bundle: Messages, key: string): string {
  const segments = key.split('.');
  let cursor: unknown = bundle;
  for (const segment of segments) {
    if (cursor && typeof cursor === 'object' && segment in cursor) {
      cursor = (cursor as Record<string, unknown>)[segment];
    } else {
      return key;
    }
  }
  return typeof cursor === 'string' ? cursor : key;
}

function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => {
    const v = vars[name];
    return v === undefined ? `{{${name}}}` : String(v);
  });
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function detectInitialLocale(): Locale {
  const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
  if (tgLang?.startsWith('tr')) return 'tr';
  if (typeof navigator !== 'undefined' && navigator.language?.startsWith('tr')) {
    return 'tr';
  }
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocale] = useState<Locale>(() => detectInitialLocale());

  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>): string => {
      const template = resolve(BUNDLES[locale], key);
      return vars ? interpolate(template, vars) : template;
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used inside <I18nProvider>');
  }
  return ctx;
}

export function useT(): I18nContextValue['t'] {
  return useI18n().t;
}
